import {
  TLAssetId,
  useEditor,
  useValue,
  TLAsset,
  getHashForString,
  AssetRecordType,
  TLImageAsset,
  createShapeId,
  PageRecordType,
  getIndexBelow,
  IndexKey,
  getIndexAbove,
  TLShapeId,
  TLShape,
  getIndexBetween,
  sortByIndex,
  TLDrawShapeSegment,
  useToasts,
  Box,
  TLParentId,
} from "tldraw"
import { Toolbar, ToolbarIconButton } from "./Toolbar"
import {
  Combine,
  Copy,
  Edit2Icon,
  Layers,
  Move,
  MoveDown,
  MoveUp,
  SquareStack,
  Trash2,
  Wand,
} from "lucide-react"
import { StickerShape, Transform } from "../../shared/StickerShape"
import { Var } from "../icons/Var"
import { StickerParametersButton } from "./StickerParametersButton"
import { ForwardedRef, forwardRef, useState } from "react"
import { GoogleGenAI } from "@google/genai"
import { editorToImage } from "./getSvgAsImage"
import { getNewLayerIndex } from "../util/functions"
import { LDPage } from "../../shared/LDPage"
import { Color, Expr } from "../../shared/Expr"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import { Layer, LayerProps } from "../../shared/Layer"
import { Parameter } from "../../shared/Parameter"
import {
  defaultTransformation,
  TransformationButton,
} from "./TransformationButton"
import {
  CloneOptions,
  CustomDrawShape,
  CustomDrawShapeProps,
} from "../../shared/CustomDrawShape"

export let transforming = false
import { IonGrid, IonItem, IonList } from "@ionic/react"
import { StrokeWidthNum } from "./LayerButton"
import { add, noop } from "lodash"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"

import Popover from "@mui/material/Popover"
import Tooltip from "@mui/material/Tooltip"
import React from "react"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { ToolbarButton } from "./ToolbarButton"
import { Slider, styled } from "@mui/material"

import { Path, Point, Shape, unionFillShapes } from "../tools/MarkerDrawing"

const ai = new GoogleGenAI({
  apiKey: "AIzaSyBGYAqHekowwYXOsbYENG_GmB4yIDrJGzE",
})

function base64ToDataUrl(base64: string): string {
  return `data:image/png;base64,${base64}`
}
function base64ToFile(
  base64String: string,
  fileName: string,
  mimeType: string = "image/png",
) {
  // Decode the Base64 string
  const binaryString = atob(base64String)

  // Convert the binary string to a Uint8Array
  const binaryData = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    binaryData[i] = binaryString.charCodeAt(i)
  }

  // Create and return a File object
  return new File([binaryData], fileName, { type: mimeType })
}

export const SelectionToolbar = forwardRef(function SelectionToolbar(
  _: {},
  ref: ForwardedRef<HTMLDivElement>,
) {
  const editor = useEditor()
  const currentPage = useCurrentPage()
  const { addToast } = useToasts()
  const [changeLayerStickerDialogOpen, setChangeLayerStickerDialogOpen] =
    useState(false)

  async function enhance() {
    const imagePath = editorToImage(editor, [...editor.getSelectedShapeIds()], {
      format: "png",
      background: true,

      padding: 2,
    })

    const imageBlob = await imagePath

    const arrayBuffer = await imageBlob.blob.arrayBuffer()
    const imageData = new Uint8Array(arrayBuffer)
    const base64Image = btoa(String.fromCharCode(...imageData))

    const contents = [
      {
        text: `This is a task for a coloring book style image enhancement. You will receive an image and progressively enhance it while maintaining a clear, coloring-book aesthetic.

Enhancement Process:


Initial Image: Start with the provided image.

The first thing to do is straighten the lines and make them more presentable. The image should be a simple line drawing, similar to a coloring book page.

Details: Keep the overall image simple, avoiding complex details or shading.
Gradual Detail Addition: In each subsequent iteration, add one or two subtle details to the image. Examples include:
- Add details so that the crappy drawing looks a little more presentable
- Smoothing lines
- Adding a door if it is a house
- Adding a face if it is a person
- Adding folds/ wrinkles if it is a piece of clothing or paper
- Adding a window if it is a building
- etc

No Background: Maintain a clean white background. Do not add any background elements.
Professional Look: While keeping the coloring-book style, ensure the image looks clean and professional.
Maintain the Spirit: Keep the fundamental spirit of the original image, simply making it more refined and detailed in a simple way.
Output:

Provide each enhanced iteration as a new image. Do not include text descriptions with the images.

Goal:

Progressively enhance the image while staying true to the coloring-book style and maintaining a clean, professional look. Focus on adding one or two simple details per iteration.`,
      },
      {
        inlineData: {
          mimeType: "image/png",

          data: base64Image,
        },
      },
    ]
    const response = await ai.models.generateContent({
      model: "gemini-2.0-flash-exp-image-generation",
      contents,
      config: {
        responseModalities: ["Text", "Image"],
      },
    })

    const base64 =
      response.candidates?.[0].content?.parts?.[0].inlineData?.data ?? ""

    const file = base64ToFile(base64 ?? "", "imageFile")

    //setTestImgSrc(response.candidates?.[0]?.content?.parts[0].inlineData.data)

    const assetId: TLAssetId = AssetRecordType.createId(
      getHashForString(base64),
    )

    const asset: TLImageAsset = {
      id: assetId,
      type: "image",
      typeName: "asset",
      props: {
        src: base64ToDataUrl(base64),
        mimeType: file.type,
        name: file.name,
        w: 0,
        h: 0,
        isAnimated: false,
        fileSize: file.size,
      },
      meta: {
        isDeleted: false,
      },
    }

    const shapeId = createShapeId()

    editor.uploadAsset(asset, file).then((uploaded) => {
      const placeholderAsset: TLAsset = {
        id: assetId,
        typeName: "asset",
        type: "image",
        props: {
          w: imageBlob.height,
          h: imageBlob.width,
          name: asset.props.name ?? "Untitled",
          isAnimated: false,
          mimeType: asset.props.mimeType,
          src: uploaded.src ?? "",
        },
        meta: {},
      }
      editor.createAssets([placeholderAsset])
    })

    // const paths = svgStringToPaths(result)

    // paths.map(path => {
    //   const shapeId2 = createShapeId()

    //   editor.createShape({

    //     type: 'custom-draw',
    //     id: shapeId2,

    //     props: {

    //       isComplete: true,

    //       segments: [

    //         {
    //           type: 'free',
    //           points: path,
    //         } as any
    //       ],
    //       layer: layerId,
    //     }

    //   })
    // })

    editor.createShape({
      type: "image",
      id: shapeId,
      props: {
        w: imageBlob.width,
        h: imageBlob.height,

        assetId,
      },
    })

    // editor.createShapes<TLImageShape>([
    //   {
    //     id: shapeId,
    //     type: 'image',

    //     props: {

    //       w: 100,
    //       h: 100,
    //       assetId,
    //     },
    //   },
    // ])

    // editor.createShape({
    //   type: 'image',
    //   props: {
    //     src: response.candidates?.[0]?.content ?? '',
    //     x: 100,
    //     y: 100,
    //     width: 200,
    //     height: 200,
    //   }

    // })
  }

  const selectedSticker = useValue(
    "selected sticker",
    () => {
      const shape = editor.getOnlySelectedShape()

      if (!shape || shape.type !== "custom-group") return null

      return shape as LDGroupShape
    },
    [editor],
  )

  const selectedShapeIds = useValue(
    "selected shape ids",
    () => editor.getSelectedShapeIds(),
    [editor],
  )

  const stickerParameters = useValue(
    "sticker parameters",
    () => {
      if (selectedSticker) {
        const stickerPage = editor.getPage(
          LDShape.getSourcePageId(editor, selectedSticker),
        ) as LDPage
        const stickerOverrides = selectedSticker.props.parameterValues
        return Object.fromEntries(
          Object.entries(stickerPage.meta.parameters).map(([id, p]) => {
            return [
              id,
              {
                name: p.name,
                type: p.type,
                value: stickerOverrides?.[id] ?? null,
              },
            ] as const
          }),
        )
      }
      return null
    },
    [selectedSticker, editor],
  )

  function handleMergeShapes() {
    const selectedShapeIds = editor.getSelectedShapeIds()
    const shapesAreFills = selectedShapeIds.every((shapeId) => {
      const shape = editor.getShape(shapeId) as CustomDrawShape
      return shape.props.isFill
    })
    const shapesAreStrokes = selectedShapeIds.every((shapeId) => {
      const shape = editor.getShape(shapeId) as CustomDrawShape
      return !shape.props.isFill
    })
    if (!shapesAreFills && !shapesAreStrokes) {
      addToast({
        title: "Cannot Merge Shapes",
        description: "You can only merge shapes of the same type.",
      })
      return null
    }
    console.log(shapesAreFills)

    const selectedBounds = editor.getSelectionPageBounds()
    if (!selectedBounds) return
    const newShapeId = createShapeId()
    const selectedLayerIds = selectedShapeIds
      .map((shapeId) => {
        const shape = editor.getShape(shapeId) as CustomDrawShape
        return shape.props.layer
      })
      .filter((layerId, i, self) => self.indexOf(layerId) === i)
    const layerId = selectedLayerIds[0]

    const layers = currentPage.meta.layers as Record<string, LayerProps>
    const layer: LayerProps = layers[layerId]

    if (selectedLayerIds.length !== 1) return

    const firstShape = editor.getShape(selectedShapeIds[0]) as CustomDrawShape
    const color = firstShape.props.isFill
      ? firstShape?.props.parameterValues.fillColor
      : firstShape.props.parameterValues.strokeColor

    if (shapesAreFills) {
      const fillShapes: Shape[] = []

      for (const shapeId of selectedShapeIds) {
        const shape = editor.getShape(shapeId) as CustomDrawShape
        const fillSegments: Path[] = []
        for (const segment of shape.props.segments) {
          const globalPoints = segment.points.map((pt) => [
            pt.x + shape.x,
            pt.y + shape.y,
          ])

          fillSegments.push(globalPoints as Point[])
        }
        fillShapes.push(fillSegments)
      }
      const unionedPaths = unionFillShapes(fillShapes as Shape[])
      console.log("Unioned Paths:", unionedPaths)
      const index = layer.index
      editor.deleteShapes(selectedShapeIds)
      editor.createShape<CustomDrawShape>({
        id: newShapeId,
        type: "custom-draw",
        x: selectedBounds.x,
        y: selectedBounds.y,
        index,
        props: {
          isFill: true,
          isComplete: true,
          segments: unionedPaths.map((path) => ({
            type: "free",
            points: path.map((pt) => ({
              x: pt[0] - selectedBounds.x,
              y: pt[1] - selectedBounds.y,
            })),
          })),
          layer: layerId,
          parameterValues: {
            ...firstShape.props.parameterValues,
            fillColor: color,
            visible: Expr.lit(true),
          },
        },
      })
    } else {
      if (!firstShape.props.isFill) {
        const mergedSegments: TLDrawShapeSegment[] = []

        for (const shapeId of selectedShapeIds) {
          const shape = editor.getShape(shapeId) as CustomDrawShape
          for (const segment of shape.props.segments) {
            const globalPoints = segment.points.map((pt) => ({
              x: pt.x + shape.x,
              y: pt.y + shape.y,
            }))

            mergedSegments.push({
              type: segment.type,
              points: globalPoints,
            })
          }
        }

        const index = layer.index

        const strokeWidth = firstShape.props.parameterValues.strokeWidth
        editor.deleteShapes(selectedShapeIds)
        editor.createShape<CustomDrawShape>({
          id: newShapeId,
          type: "custom-draw",
          x: selectedBounds.x,
          y: selectedBounds.y,
          index,
          props: {
            isFill: false,
            isComplete: true,
            segments: mergedSegments.map((seg) => ({
              type: seg.type,
              points: seg.points.map((pt) => ({
                x: pt.x - selectedBounds.x,
                y: pt.y - selectedBounds.y,
              })),
            })),
            layer: layerId,
            parameterValues: {
              ...firstShape.props.parameterValues,
              strokeColor: color,
              strokeWidth: Expr.lit(
                Expr.evaluate<number>(
                  strokeWidth,
                  currentPage.meta.parameterValues,
                ),
              ),
              visible: Expr.lit(true),
            },
          },
        })
      }
    }
    addToast({
      title: "Merged Shapes",
      description: `Merged ${selectedShapeIds.length} shapes into one.`,
    })
  }

  function handleDeleteShapes() {
    const selectedShapeIds = editor.getSelectedShapeIds()
    // delete the bound fill
    const getFillsBoundToShape = (shapeId: TLShapeId) => {
      return editor
        .getBindingsInvolvingShape(shapeId)
        .filter((binding) => binding.type === "fill")
        .map((binding) => binding.fromId)
    }
    selectedShapeIds.forEach((shapeId) => {
      const fillsBoundToShape = getFillsBoundToShape(shapeId)
      fillsBoundToShape.forEach((fillId) => {
        editor.deleteShape(fillId)
      })
    })

    editor.deleteShapes(selectedShapeIds)
  }

  function handleAbstractShapes() {
    const selectionBounds = editor.getSelectionPageBounds()
    if (!selectionBounds) return
    const selectedShapeIds = editor.getSelectedShapeIds()
    const page = editor.getPage(editor.getCurrentPageId()) as LDPage
    const index = getIndexBelow(page.index)

    const selectedShapes = selectedShapeIds.map((shapeId) => {
      const shape = editor.getShape(shapeId) as LDShape
      return shape
    })

    const selectedLayers = selectedShapes.map((shape) => {
      const layer = page.meta.layers[LDShape.getLayerId(shape as LDShape)]
      return layer
    })

    const newPageId = PageRecordType.createId()
    if (selectedShapeIds.length > 0) {
      const relevantPageParamsList = selectedShapes
        ?.filter((shape) => shape.type === "custom-group")
        .flatMap((s, i) => {
          return Object.entries(s.props.parameterValues)
            .filter(([_, val]) => val.type === "ref")
            .map(([id, value]) => {
              if (value.type !== "ref") throw new Error()
              return [
                `${value.id}`,
                {
                  // defaultValue: page.meta.parameters[value.id].defaultValue,
                  name: `${page.meta.parameters[value.id].name}`,
                  type: page.meta.parameters[value.id].type,
                },
              ] as [string, Parameter]
            })
        })

      const parameters = relevantPageParamsList

      const parameterValues = page.meta.parameterValues

      // selectedLayers.map<LayerProps>((layer) => {
      //   return {
      //     ...layer,
      //     id: layer.id,
      //     color: layer.isFill
      //       ? Expr.ref(`parameter-${layer.id}Color`)
      //       : Expr.lit(Expr.evaluate(layer.color, page.meta.parameterValues)),
      //   }
      // })

      const pages = Object.values(editor.getPages())
      const sortedPages = pages.sort((a, b) => sortByIndex(a, b))

      const pageIndex = sortedPages.findIndex((p) => p.id === page.id)

      const abstractedPageIndex = getIndexBetween(
        sortedPages[pageIndex - 1]?.index ?? getIndexBelow(page.index),
        page.index,
      )

      editor.createPage({
        id: newPageId,
        name: "New Page",
        index: abstractedPageIndex,
        meta: {
          currentLayer: Layer.layer1.id,
          layers: { layer1: Layer.layer1 },

          parameters: {},
          parameterValues: {},
          origin: { x: 0, y: 0 },
        },
      })

      editor.setCurrentPage(newPageId)

      editor.updateShapes(
        selectedShapeIds
          .filter((s) => {
            return editor.getShape(s)?.parentId === page.id
          })
          .map((shapeId) => {
            const shape = editor.getShape(shapeId)
            return {
              ...shape,
              id: shapeId,
              parentId: newPageId,

              type: shape!.type,
            }
          }),
      )

      //editor.setCurrentPage(page.id)

      const id = createShapeId()

      // const xy = selectedShapesXy.reduce((acc, curr) => {
      //   return {
      //     x: Math.min(acc.x, curr.x),
      //     y: Math.min(acc.y, curr.y),
      //   }
      // }, { x: Infinity, y: Infinity })
      const xy = { x: selectedShapes[0]?.x, y: selectedShapes[0]?.y }

      const globalX = xy.x
      const globalY = xy.y
      const shapeId = createShapeId()

      const shapeIds = editor.getPageShapeIds(newPageId)

      let commonBounds: Box | undefined
      ;[...shapeIds]
        .filter((id) => {
          const shape = editor.getShape(id)!
          return true //shape.meta.pageId === pageId || shape.props.pageId === pageId
        })
        .forEach((shapeId) => {
          const bounds = editor.getShapeMaskedPageBounds(shapeId)
          if (!bounds) return
          if (!commonBounds) {
            commonBounds = bounds.clone()
          } else {
            commonBounds = commonBounds.expand(bounds)
          }
        })

      const newShapes = [...shapeIds]
        .map((id) => editor.getShape(id))
        .filter((shape) => !(shape as CustomDrawShape)?.props?.isClone)
        .map((shape) => {
          const newId = shape?.id + shapeId

          const shapeOffsetX = shape!.x - commonBounds!.x
          const shapeOffsetY = shape!.y - commonBounds!.y
          console.log({ shape })
          return {
            ...(shape as TLShape),
            id: newId as TLShapeId,
            x:
              shape!.type !== "custom-group"
                ? shapeOffsetX + globalX // - shapeOffsetX
                : shape!.x,
            y:
              shape!.type !== "custom-group"
                ? shapeOffsetY + globalY //- shapeOffsetY
                : shape!.y,
          }
        })

      editor.createShapes(newShapes)

      const targetPage = editor.getPage(newPageId) as LDPage

      editor.createShapes<LDGroupShape>([
        {
          id: shapeId,
          type: "custom-group",
          parentId: page.id,

          x: globalX,
          y: globalY,
          opacity: 1,
          props: {
            layer: page.meta.currentLayer,
            pageId: newPageId,
            parameterValues: targetPage.meta.parameterValues,
          },
        },
      ])

      // Should reparent only top level shapes but copy all shapes
      const topLevelShapes = newShapes.filter((s) => s.parentId === newPageId)
      editor.reparentShapes(topLevelShapes, shapeId)

      newShapes
        .filter((s) => s.parentId !== newPageId)
        .forEach((shape) => {
          editor.updateShape({
            ...shape,
            parentId: (shape.parentId + shapeId) as TLParentId,
          })
        })
      // editor.createBinding({
      //   id: createShapeId(),
      //   type: 'line',
      //   startShapeId: id,
      //   endShapeId: selectedShapeIds[0],
      //   startHandleIndex: 0,
      //   endHandleIndex: 0,
      // })
    }
  }
  function moveShapeDownStack() {
    const shapes = editor.getCurrentPageShapes()
    const selectedShapes = selectedShapeIds.map((shapeId) => {
      const shape = editor.getShape(shapeId) as LDShape
      return shape
    })
    const layerArray = Object.values(page.meta.layers)
    const shapeIndexes = shapes.map((shape) => shape.index).sort()
    const layerAndShapeIndexArray = shapeIndexes
      .concat(layerArray.map((layer) => layer.index))
      .sort()
    console.log("our shape", selectedShapes[0].index)
    console.log("first", shapeIndexes)

    const shapeIndexPosition = layerAndShapeIndexArray.findIndex(
      (index) => index === selectedShapes[0].index,
    )
    const newIndex =
      //is there even an index above the index of the current shape
      layerAndShapeIndexArray[shapeIndexPosition - 1] &&
      // is the next index a shape (if its not its a layer)

      shapes.findIndex(
        (shape) =>
          shape.index === layerAndShapeIndexArray[shapeIndexPosition - 1],
      ) !== -1
        ? // is there a shape or layer two indexes above current one (if so we must place it in between that and and the one 1 above)
          layerAndShapeIndexArray[shapeIndexPosition - 2]
          ? getIndexBetween(
              layerAndShapeIndexArray[shapeIndexPosition - 2],
              layerAndShapeIndexArray[shapeIndexPosition - 1],
            )
          : getIndexBelow(layerAndShapeIndexArray[shapeIndexPosition + 1])
        : selectedShapes[0].index

    console.log("new Index", newIndex)

    editor.updateShape({
      ...selectedShapes[0],

      index: newIndex,
    })
  }
  function moveShapeUpStack() {
    const shapes = editor.getCurrentPageShapes()
    const selectedShapes = selectedShapeIds.map((shapeId) => {
      const shape = editor.getShape(shapeId) as LDShape
      return shape
    })
    const layerArray = Object.values(page.meta.layers)
    const shapeIndexes = shapes.map((shape) => shape.index).sort()
    const layerAndShapeIndexArray = shapeIndexes
      .concat(layerArray.map((layer) => layer.index))
      .sort()

    console.log("our shape", selectedShapes[0].index)

    console.log("shape indexes", shapeIndexes)

    const shapeIndexPosition = layerAndShapeIndexArray.findIndex(
      (index) => index === selectedShapes[0].index,
    )
    const newIndex =
      //is there even an index above the index of the current shape
      layerAndShapeIndexArray[shapeIndexPosition + 1] &&
      // is the next index a shape (if its not its a layer)

      shapes.findIndex(
        (shape) =>
          shape.index === layerAndShapeIndexArray[shapeIndexPosition + 1],
      ) !== -1
        ? // is there a shape or layer two indexes above current one (if so we must place it in between that and and the one 1 above)
          layerAndShapeIndexArray[shapeIndexPosition + 2]
          ? getIndexBetween(
              layerAndShapeIndexArray[shapeIndexPosition + 1],
              layerAndShapeIndexArray[shapeIndexPosition + 2],
            )
          : getIndexAbove(layerAndShapeIndexArray[shapeIndexPosition + 1])
        : selectedShapes[0].index

    console.log("new Index", newIndex)

    editor.updateShape({
      ...selectedShapes[0],

      index: newIndex,
    })
  }

  function moveStickerUpStack() {
    if (selectedSticker) {
      const page = editor.getPage(
        LDShape.getSourcePageId(editor, selectedSticker),
      ) as LDPage

      const stickerArray = editor
        .getCurrentPageShapes()
        .filter((shape) => shape.type === "custom-group") as TLShape[]
      const stickerIndexArray = stickerArray.map((shape) => shape.index)
      const selectedStickerIndex = selectedSticker.index

      const layerArray = Object.values(page.meta.layers)
      const layerAndStickerIndexArray = stickerIndexArray
        .concat(layerArray.map((layer) => layer.index))
        .sort()
      const stickerIndexPosition = layerAndStickerIndexArray.findIndex(
        (index) => index === selectedStickerIndex,
      )

      const newIndex = layerAndStickerIndexArray[stickerIndexPosition + 1]
        ? layerAndStickerIndexArray[stickerIndexPosition + 2]
          ? getIndexBetween(
              layerAndStickerIndexArray[stickerIndexPosition + 1],
              layerAndStickerIndexArray[stickerIndexPosition + 2],
            )
          : getIndexAbove(layerAndStickerIndexArray[stickerIndexPosition + 1])
        : selectedStickerIndex

      const layersIndexArrayWithNewIndex = layerArray
        .map((layer) => layer.index)
        .concat(newIndex)
        .sort()

      const layerIndex = layersIndexArrayWithNewIndex.findIndex(
        (index) => index === newIndex,
      )
      const newLayer = layerArray[layerIndex - 1]

      editor.updateShape({
        ...selectedSticker,
        meta: {
          ...selectedSticker.meta,
          layer: newLayer.id,
        },
        index: newIndex,
      })
    }
  }

  function moveStickerDownStack() {
    if (selectedSticker) {
      const page = editor.getCurrentPage() as LDPage

      const stickerArray = editor
        .getCurrentPageShapes()
        .filter((shape) => shape.type === "custom-group") as TLShape[]
      const stickerIndexArray = stickerArray.map((shape) => shape.index)
      const selectedStickerIndex = selectedSticker.index

      const layerArray = Object.values(page.meta.layers)
      const layerAndStickerIndexArray = stickerIndexArray
        .concat(layerArray.map((layer) => layer.index))
        .sort()
      const stickerIndexPosition = layerAndStickerIndexArray.findIndex(
        (index) => index === selectedStickerIndex,
      )

      const newIndex = layerAndStickerIndexArray[stickerIndexPosition - 1]
        ? layerAndStickerIndexArray[stickerIndexPosition - 2]
          ? getIndexBetween(
              layerAndStickerIndexArray[stickerIndexPosition - 2],
              layerAndStickerIndexArray[stickerIndexPosition - 1],
            )
          : selectedStickerIndex
        : selectedStickerIndex

      const layersIndexArrayWithNewIndex = layerArray
        .map((layer) => layer.index)
        .concat(newIndex)
        .sort()

      const layerIndex = layersIndexArrayWithNewIndex.findIndex(
        (index) => index === newIndex,
      )
      const newLayer = layerArray[layerIndex - 1]

      editor.updateShape({
        ...selectedSticker,
        meta: {
          ...selectedSticker.meta,
          layer: newLayer.id,
        },
        index: newIndex,
      })
    }
  }

  function handleEditSticker() {
    if (selectedSticker) {
      editor.setCurrentPage(LDShape.getSourcePageId(editor, selectedSticker))
    }
  }

  function handleStickerParamChange(
    paramId: string,
    value: Expr<Color> | Expr<number> | Expr<boolean>,
  ) {
    if (selectedSticker) {
      editor.updateShape({
        ...selectedSticker,
        props: {
          ...selectedSticker.props,
          parameterValues: {
            ...selectedSticker.props.parameterValues,
            [paramId]: value,
          },
        },
      })
    }
  }
  const page = editor.getPage(editor.getCurrentPageId()) as LDPage
  // const selectedShapeLayers = selectedShapeIds
  //   .map((shapeId) => {
  //     const shape = editor.getShape(shapeId)
  //     console.log({ shape })

  //     return page.meta.layers[LDShape.getLayerId(shape as LDShape)].id
  //   })
  //   .filter((layerId, index, self) => {
  //     return self.indexOf(layerId) === index
  //   })

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // function handleTransformChange(
  //   transformation: keyof Transform,
  //   value: Expr<number>,
  // ) {
  //   const shapeId = selectedShapeIds[0]
  //   const shape = editor.getShape(shapeId) as CustomDrawShape
  //   if (!shape) throw new Error(`Can't find shape with id ${shapeId}`)
  //   let orientation = undefined
  //   if (!shape.props?.baseOrientation) {
  //     orientation = {
  //       x: shape.x,
  //       y: shape.y,
  //       rotation: shape.rotation,
  //       scaleX: 1,
  //       scaleY: 1,
  //       segments: shape.props.segments,
  //     }
  //   }
  //   transforming = true
  //   editor.updateShape({
  //     id: shapeId,
  //     props: {
  //       ...shape.props,
  //       baseOrientation: orientation ?? shape.props.baseOrientation,
  //       transform: {
  //         ...shape.props.transform,
  //         [transformation]: value,
  //       },
  //     },
  //     type: shape.type,
  //   })
  //   transforming = false
  // }

  function handleCloneShape(clone: CloneOptions) {
    console.log({ clone })
    const selectedShape = editor.getOnlySelectedShape()
    if (
      !selectedShape ||
      (selectedShape.type !== "custom-draw" &&
        selectedShape.type !== "custom-group")
    )
      return
    if (selectedShape.type === "custom-group") {
      editor.updateShape<LDGroupShape>({
        ...selectedShape,
        type: selectedShape.type,
        props: {
          ...selectedShape.props,
          clone: clone,
        },
      })
    } else {
      editor.updateShape<CustomDrawShape | LDGroupShape>({
        ...selectedShape,
        type: selectedShape.type,
        props: {
          ...selectedShape.props,
          clone: clone,
        },
      })
    }
  }

  const onlySelectedShape = useValue(
    "only selected shape",
    () => {
      return editor.getOnlySelectedShape() ?? null
    },
    [editor],
  )

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {/* <LayerSelectionList selectedLayerIds={selectedShapeLayers} /> */}
      {/* <SelectionListDiv>{selectedShapeIds.length} selected</SelectionListDiv> */}
      <div style={{ display: "flex", flexDirection: "row" }} ref={ref}>
        {selectedShapeIds.length > 1 && (
          <Tooltip title="Merge">
            <ToolbarIconButton icon={Combine} onClick={handleMergeShapes} />
          </Tooltip>
        )}
        {/* {selectedShapeIds.length === 1 && onlySelectedShape && (
          <Tooltip title="Clone">
            <TransformationButton
              transform={
                (onlySelectedShape.type === "custom-draw"
                  ? (onlySelectedShape.props as CustomDrawShapeProps)?.clone
                      ?.transformation
                  : (onlySelectedShape as LDGroupShape).props?.clone
                      ?.transformation) ?? defaultTransformation
              }
              shapeId={selectedShapeIds[0]}
              onChange={(key, value) => {
                console.log("trying")
                handleCloneShape({
                  num:
                    (onlySelectedShape.type === "custom-draw"
                      ? (onlySelectedShape.props as CustomDrawShapeProps)?.clone
                          ?.num
                      : (onlySelectedShape as LDGroupShape).props?.clone
                          ?.num) ?? Expr.lit(10),
                  transformation: {
                    ...((onlySelectedShape.type === "custom-draw"
                      ? (onlySelectedShape.props as CustomDrawShapeProps)?.clone
                          ?.transformation
                      : (onlySelectedShape as LDGroupShape).props?.clone
                          ?.transformation) ?? defaultTransformation),
                    [key]: value,
                  },
                })
              }}
              num={
                onlySelectedShape.type === "custom-draw"
                  ? (onlySelectedShape.props as CustomDrawShapeProps)?.clone
                      ?.num
                  : (onlySelectedShape as LDGroupShape).props?.clone?.num
              }
              onChangeNum={(num) => {
                handleCloneShape({
                  num,
                  transformation:
                    (onlySelectedShape.type === "custom-draw"
                      ? (onlySelectedShape.props as CustomDrawShapeProps)?.clone
                          ?.transformation
                      : (onlySelectedShape as LDGroupShape).props?.clone
                          ?.transformation) ?? defaultTransformation,
                })
              }}
            >
              {(clickHandler, ref) => (
                <ToolbarIconButton
                  ref={ref}
                  icon={SquareStack}
                  onClick={clickHandler}
                />
              )}
            </TransformationButton>
          </Tooltip>
        )} */}
        <Tooltip title="Abstract">
          <ToolbarIconButton icon={Copy} onClick={handleAbstractShapes} />
        </Tooltip>
        {!selectedSticker && (
          <Tooltip title="Enhance">
            <ToolbarIconButton icon={Wand} onClick={enhance} />
          </Tooltip>
        )}
        {onlySelectedShape && (
          <>
            <Tooltip title="Adjust Position In Stack">
              <ToolbarIconButton icon={Layers} onClick={handleClick} />
            </Tooltip>
            <Popover
              anchorReference="anchorPosition"
              anchorPosition={
                anchorEl
                  ? {
                      top: anchorEl?.getBoundingClientRect().top! - 60,
                      left: anchorEl?.getBoundingClientRect().left! + 60,
                    }
                  : undefined
              }
              open={anchorEl !== null}
              anchorEl={anchorEl}
              onClose={handleClose}
            >
              <Toolbar direction="horizontal">
                <Tooltip
                  title={
                    selectedSticker
                      ? "Move Sticker Up Stack"
                      : "Move Shape Up Stack"
                  }
                >
                  <ToolbarIconButton
                    icon={MoveUp}
                    onClick={
                      selectedSticker ? moveStickerUpStack : moveShapeUpStack
                    }
                  />
                </Tooltip>
                <Tooltip
                  title={
                    selectedSticker
                      ? "Move Sticker Down Stack"
                      : "Move Shape Down Stack"
                  }
                >
                  <ToolbarIconButton
                    icon={MoveDown}
                    onClick={
                      selectedSticker
                        ? moveStickerDownStack
                        : moveShapeDownStack
                    }
                  />
                </Tooltip>
              </Toolbar>
            </Popover>
          </>
        )}
        {/* {selectedShapeIds.length === 1 && (
          <Tooltip title={"Transform"}>
            <TransformationButton
              transform={onlySelectedShape.props.transform}
              shapeId={selectedShapeIds[0]}
              onChange={handleTransformChange}
            >
              {(clickHandler, ref) => (
                <ToolbarIconButton
                  ref={ref}
                  icon={Move}
                  onClick={clickHandler}
                />
              )}
            </TransformationButton>
          </Tooltip>
        )} */}

        {selectedSticker && (
          <>
            <Tooltip title="Edit Sticker">
              <ToolbarIconButton icon={Edit2Icon} onClick={handleEditSticker} />
            </Tooltip>
            {/* <Tooltip title="Sticker Parameters">
              <StickerParametersButton
                onChange={handleStickerParamChange}
                parameters={stickerParameters!}
              >
                {(clickHandler, ref) => (
                  <ToolbarIconButton
                    ref={ref}
                    icon={Var}
                    onClick={clickHandler}
                  />
                )}
              </StickerParametersButton>
            </Tooltip> */}
          </>
        )}
        <Tooltip title={selectedSticker ? "Delete Sticker" : "Delete Shape"}>
          <ToolbarIconButton icon={Trash2} onClick={handleDeleteShapes} />
        </Tooltip>

        {/* <Tooltip content='Settings'>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <ToolbarIconButton icon={Ellipsis} onClick={() => undefined} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content side='right'>
            <DropdownMenu.Item onSelect={() => undefined}><Copy size={14} /> Duplicate</DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => undefined}><BringToFront size={14} /> Bring to Front</DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => undefined}><SendToBack size={14} /> Send to Back</DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Tooltip> */}
      </div>
    </div>
  )
})

type LayerSelectionListProps = {
  selectedLayerIds: string[]
}
function LayerSelectionList({ selectedLayerIds }: LayerSelectionListProps) {
  const page = useCurrentPage()

  return (
    <SelectionListDiv>
      {/* 3 selected */}
      {Object.values(page.meta.layers)
        .filter((layer) => selectedLayerIds.includes(layer.id))
        .map((layer) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // TODO(alvaro): 3d layer experiment, probably not worth it
              // transform: "rotateY(35deg)",
              // border: "1px solid #bbb",
              // marginRight: -25,
              // background: "white",
              // padding: "2px 8px",
            }}
          >
            {layer.name}
          </div>
        ))}
    </SelectionListDiv>
  )
}

export const SelectionListDiv = styled("div")({
  padding: "4px 12px",
  background: "#eee",
  borderRadius: "4px 4px 0 0",
  display: "flex",
  alignItems: "center",
  gap: 4,
})
